import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import 'react-quill/dist/quill.snow.css';
import { FaTrash } from 'react-icons/fa6';
import { useDebounce } from '../../../../utils/debounce.utils';
import { useSelectWithParams } from '../../../../utils/selector.utils';
import { companyContextsSelectors } from '../../../../app/resource/company-contexts/company-contexts.selectors';
import {
  deleteCompanyContext,
  updateCompanyContext,
} from '../../../../app/page/company-context/company-context.actions';
import { closeDialog, openDialog } from '../../../../app/dialog/dialog.actions';
import { CustomQuillEditor } from '../../common/QuillEditor';

interface ISectionProps {
  companyContextId: string;
}

interface IDraft {
  title: string;
  summary: string;
  content: string;
}

export const Section: FunctionComponent<ISectionProps> = ({ companyContextId }) => {
  const dispatch = useDispatch();
  const companyContext = useSelectWithParams(companyContextsSelectors.selectResourceById, companyContextId);
  // Initialize draft lazily from the store and then treat the local draft as the source of
  // truth. Re-syncing from `companyContext` on every change would clobber in-progress typing with the
  // echo from a completed debounced save.
  const [draft, setDraft] = useState<IDraft>(() => ({
    title: companyContext?.title ?? '',
    summary: companyContext?.summary ?? '',
    content: companyContext?.content ?? '',
  }));

  const saveDraftNow = (id: string, payload: IDraft) => {
    dispatch(updateCompanyContext(id, payload));
  };

  const saveDraftDebounce = useDebounce(
    (id: string, payload: IDraft) => {
      dispatch(updateCompanyContext(id, payload));
    },
    2000,
    [dispatch],
  );

  useEffect(() => {
    return () => {
      saveDraftDebounce.flush();
    };
  }, [companyContextId, saveDraftDebounce]);

  if (!companyContext) {
    return null;
  }

  const handleDraftChange = (field: 'title' | 'summary' | 'content') => (value: string) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      saveDraftDebounce(companyContext.id, next);
      return next;
    });
  };

  const handleDraftBlur = (field: 'title' | 'summary' | 'content') => (value: string) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      saveDraftNow(companyContext.id, next);
      saveDraftDebounce.cancel();
      return next;
    });
  };

  const handleDelete = () => {
    dispatch(
      openDialog('confirmation', {
        title: 'Delete this section?',
        message: 'This action cannot be undone.',
        onConfirm: () => {
          dispatch(closeDialog('confirmation'));
          saveDraftDebounce.cancel();
          dispatch(deleteCompanyContext(companyContext.id));
        },
        onDeny: () => dispatch(closeDialog('confirmation')),
        styles: { zIndex: '999999999', position: 'fixed' },
      }),
    );
  };

  return (
    <div className="panel mb-3">
      <div className="panel-body">
        <div className="flex aic mb-2 jcsb" style={{ height: 40 }}>
          <input
            type="text"
            className="form-control flex-grow-1 mr-1 bold"
            value={draft.title}
            placeholder="Section title"
            onChange={(e) => handleDraftChange('title')(e.target.value)}
            onBlur={(e) => handleDraftBlur('title')(e.target.value)}
            aria-label="Section title"
            style={{ height: '100%', borderRadius: 0, border: '1px solid #ccc' }}
          />
          <button
            type="button"
            className="remove-button"
            onClick={handleDelete}
            aria-label="Delete section"
            style={{ width: 40, border: '1px solid #ccc' }}
          >
            <FaTrash />
          </button>
        </div>
        <div className="flex mb-2 jcsb">
          <textarea
            className="form-control mb-2"
            value={draft.summary}
            placeholder="Section summary"
            rows={3}
            onChange={(e) => handleDraftChange('summary')(e.target.value)}
            onBlur={(e) => handleDraftBlur('summary')(e.target.value)}
            aria-label="Section summary"
            style={{ borderRadius: 0, border: '1px solid #ccc', resize: 'vertical' }}
          />
        </div>
        <CustomQuillEditor
          className="company-vision-section-quill"
          key={companyContext.id}
          value={draft.content}
          onChange={handleDraftChange('content')}
          onBlur={() => handleDraftBlur('content')(draft.content)}
          style={{ height: '560px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}
        />
      </div>
    </div>
  );
};
